import { useCallback, useRef, useEffect } from 'react'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import { useVolume } from '../InPersonMeetingProvider.shared'
import * as Sentry from '@sentry/react'
import { useAudioContext } from './useAudioContext'

interface UseMicMuteDetectorProps {
  checkingTimeoutMs?: number
  muteHoldTimeoutMs?: number
  onMuteDetected?: () => void
  onUnmuteDetected?: () => void
  onMuteCallback?: () => void
  volumeThreshold?: number
  resendMuteDetectCallback?: boolean
  initialMuteCallbackDelayMs?: number
  maxMuteCallbackDelayMs?: number
}

const calculateBackoffDelay = (callbackCount: number, initialDelay: number, maxDelay: number): number => {
  if (callbackCount === 0) {
    return initialDelay
  }

  let delay: number
  if (callbackCount === 1) {
    delay = initialDelay * 6
  } else if (callbackCount === 2) {
    delay = initialDelay * 30
  } else if (callbackCount === 3) {
    delay = initialDelay * 60
  } else if (callbackCount === 4) {
    delay = initialDelay * 150
  } else {
    delay = initialDelay * 300
  }

  return Math.min(delay, maxDelay)
}

export const useMicMuteDetector = ({
  muteHoldTimeoutMs = 30000,
  onMuteDetected,
  onUnmuteDetected,
  onMuteCallback,
  volumeThreshold = 0.01,
  resendMuteDetectCallback = false,
  initialMuteCallbackDelayMs = 10000,
  maxMuteCallbackDelayMs = 120000,
}: UseMicMuteDetectorProps = {}) => {
  const logger = useLogger()
  const { setVolume } = useVolume()
  const analyserRef = useRef<AnalyserNode>()
  const streamSourceRef = useRef<MediaStreamAudioSourceNode>()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const muteDetectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const muteCallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const prevIsMutedRef = useRef<boolean>(true)
  const isMutedRef = useRef<boolean>(true)
  const muteDetectionCountRef = useRef<number>(0)
  const { audioContext, suspendAudioContext, resumeAudioContext, closeAudioContext } = useAudioContext()
  const scheduleNextMuteDetectionRef = useRef<(() => Promise<void>) | null>(null)

  const clearMuteDetectionTimeout = useCallback(async () => {
    if (muteDetectionTimeoutRef.current) {
      clearTimeout(muteDetectionTimeoutRef.current)
      muteDetectionTimeoutRef.current = null
      await logger.info('[useMicMuteDetector] Cleared mute detection timeout')
    }
  }, [logger])

  const clearMuteCallbackTimeout = useCallback(async () => {
    if (muteCallbackTimeoutRef.current) {
      clearTimeout(muteCallbackTimeoutRef.current)
      muteCallbackTimeoutRef.current = null
      await logger.info('[useMicMuteDetector] Cleared mute callback timeout')
    }
  }, [logger])

  const scheduleNextMuteDetection = useCallback(async () => {
    const currentDelay = calculateBackoffDelay(muteDetectionCountRef.current, initialMuteCallbackDelayMs, maxMuteCallbackDelayMs)

    muteDetectionTimeoutRef.current = setTimeout(async () => {
      await logger.warn(
        `[useMicMuteDetector] No sound detected for ${currentDelay / 1000} seconds (detection #${muteDetectionCountRef.current + 1}), setting isMuted=true`,
      )
      isMutedRef.current = true
      prevIsMutedRef.current = true

      await logger.info('[useMicMuteDetector] Calling onMuteDetected')
      onMuteDetected?.()
      muteDetectionCountRef.current += 1

      if (resendMuteDetectCallback && scheduleNextMuteDetectionRef.current) {
        await scheduleNextMuteDetectionRef.current()
      }
      if (onMuteCallback && !muteCallbackTimeoutRef.current) {
        muteCallbackTimeoutRef.current = setTimeout(async () => {
          await logger.info(`[useMicMuteDetector] Mute held for ${muteHoldTimeoutMs / 1000} seconds, calling onMuteCallback`)
          onMuteCallback()
        }, muteHoldTimeoutMs)
      }
    }, currentDelay)

    await logger.info(`[useMicMuteDetector] Started mute detection timeout with ${currentDelay / 1000} second delay`)
  }, [logger, onMuteDetected, resendMuteDetectCallback, onMuteCallback, muteHoldTimeoutMs, initialMuteCallbackDelayMs, maxMuteCallbackDelayMs])

  useEffect(() => {
    scheduleNextMuteDetectionRef.current = scheduleNextMuteDetection
  }, [scheduleNextMuteDetection])

  const analyseAudio = useCallback(async () => {
    if (!analyserRef?.current) {
      await logger.warn('[useMicMuteDetector] analyseAudio called but analyserRef is not set')
      return
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
    analyserRef.current.getByteFrequencyData(dataArray)

    const sum = dataArray.reduce((acc, val) => acc + val, 0)
    const average = sum / dataArray.length / 255

    const volumeLevel = average > 0 ? 1 : 0

    setVolume(volumeLevel)

    const isSoundDetected = average > volumeThreshold

    if (isSoundDetected) {
      await clearMuteDetectionTimeout()
      await clearMuteCallbackTimeout()

      if (isMutedRef.current) {
        isMutedRef.current = false

        if (prevIsMutedRef.current) {
          await logger.info('[useMicMuteDetector] Sound detected, calling onUnmuteDetected')
          onUnmuteDetected?.()
          muteDetectionCountRef.current = 0
          await logger.info('[useMicMuteDetector] Reset mute detection count to 0')
        }
        prevIsMutedRef.current = false
      }
    } else {
      if (!muteDetectionTimeoutRef.current && scheduleNextMuteDetectionRef.current) {
        await scheduleNextMuteDetectionRef.current()
        await logger.info('[useMicMuteDetector] Started mute detection timeout due to no sound detected')
      }
    }
  }, [setVolume, volumeThreshold, logger, clearMuteDetectionTimeout, clearMuteCallbackTimeout, onUnmuteDetected])

  const startAnalysing = useCallback(
    async (stream: MediaStream) => {
      if (!audioContext) {
        await logger.error('[useMicMuteDetector] AudioContext is not available')
        return
      }

      await logger.info('[useMicMuteDetector] Initializing AudioContext and AnalyserNode')

      analyserRef.current = audioContext.createAnalyser()
      analyserRef.current.fftSize = 256
      analyserRef.current.smoothingTimeConstant = 0.8

      await logger.info('[useMicMuteDetector] Starting audio analysis')
      if (audioContext.state === 'suspended') {
        await resumeAudioContext()
        await logger.info('[useMicMuteDetector] Resumed suspended AudioContext')
      }

      streamSourceRef.current = audioContext.createMediaStreamSource(stream)
      streamSourceRef.current.connect(analyserRef.current)

      intervalRef.current = setInterval(() => analyseAudio(), 100)
      await logger.info('[useMicMuteDetector] Audio analysis interval started')
    },
    [analyseAudio, logger, audioContext, resumeAudioContext],
  )

  const stopAnalysing = useCallback(async () => {
    await logger.info('[useMicMuteDetector] Stopping audio analysis')

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
      await logger.info('[useMicMuteDetector] Cleared audio analysis interval')
    }

    await clearMuteDetectionTimeout()
    await clearMuteCallbackTimeout()
    isMutedRef.current = true
    setVolume(0)
    muteDetectionCountRef.current = 0
    await logger.info('[useMicMuteDetector] Reset isMuted to true, volume to 0, and detection count to 0')
  }, [logger, clearMuteDetectionTimeout, clearMuteCallbackTimeout, setVolume])

  const _cleanup = useCallback(async () => {
    if (audioContext && audioContext.state !== 'closed') {
      await logger.info('[useMicMuteDetector] Closing AudioContext')
      await closeAudioContext()
    }

    if (streamSourceRef.current) {
      streamSourceRef.current.disconnect()
      await logger.info('[useMicMuteDetector] Disconnected stream source from analyser')
    }
  }, [logger, audioContext, closeAudioContext])

  const startListening = useCallback(
    async (stream: MediaStream) => {
      await logger.info(`[useMicMuteDetector] Starting to listen to media stream: ${stream.id}`)

      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track: MediaStreamTrack) => {
            track.stop()
            logger.info(`[useMicMuteDetector] Stopped previous track: ${track.id}`)
          })
        }

        streamRef.current = stream
        await startAnalysing(streamRef.current)

        await logger.info('[useMicMuteDetector] Listening to media stream')
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error('[useMicMuteDetector] Failed to listen to media stream', error)
        await _cleanup()
      }
    },
    [logger, startAnalysing, _cleanup],
  )

  const stopListening = useCallback(async () => {
    await logger.info('[useMicMuteDetector] Stopping listening to media stream')
    await stopAnalysing()
    await _cleanup()
    await logger.info('[useMicMuteDetector] Listening stopped')
  }, [logger, stopAnalysing, _cleanup])

  const pauseListening = useCallback(async () => {
    await logger.info('[useMicMuteDetector] Pausing listening to media stream')
    if (audioContext) {
      await suspendAudioContext()
    }
    setVolume(0)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    await clearMuteDetectionTimeout()
    await clearMuteCallbackTimeout()
    await logger.info('[useMicMuteDetector] Listening paused')
  }, [logger, audioContext, suspendAudioContext, clearMuteCallbackTimeout, clearMuteDetectionTimeout, setVolume])

  const resumeListening = useCallback(async () => {
    await logger.info('[useMicMuteDetector] Resuming listening to media stream')
    if (audioContext) {
      await resumeAudioContext()
    }
    intervalRef.current = setInterval(() => analyseAudio(), 100)
    await logger.info('[useMicMuteDetector] Listening resumed')
  }, [logger, audioContext, resumeAudioContext, analyseAudio])

  return {
    startListening,
    stopListening,
    pauseListening,
    resumeListening,
  }
}
